import {
  ApiError,
  GetTimetableStructureResponse,
  UpdateTimetableStructureRequestBody,
  useCreateTimetableStructureMutation,
  useDeleteTimetableStructureMutation,
  useGetTimetableStructureQuery,
  useUpdateTimetableStructureMutation,
} from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { isDateInPast } from '@schooly/utils/date';
import { isErrorByDetails } from '@schooly/utils/set-form-error-for-api-error';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useSchool } from '../../hooks/useSchool';
import useSchoolYears from '../../hooks/useSchoolYears';
import {
  StructureChange,
  useTimetableStructureConfirmation,
} from '../../pages/School/SchoolTimetableStructures/utils';
import { useRouter } from '../router/useRouter';

export const STRUCTURE_NAME_UNIQUENESS_ERROR = 'STRUCTURE_NAME_UNIQUENESS_ERROR';

type TimetableStructurePeriodBase = UpdateTimetableStructureRequestBody['periods'][number];

export type TimetableStructurePeriodForm = TimetableStructurePeriodBase;

export type TimetableStructureForm = Omit<UpdateTimetableStructureRequestBody, 'periods'> & {
  periods: TimetableStructurePeriodForm[];
};

export type TimetableStructureTimeSlotForm = TimetableStructureForm['time_slots'][number];

export type TimetableStructureContextProps = {
  structure?: GetTimetableStructureResponse;
  id: string;
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  canDelete: boolean;
  confirmChange: (change: StructureChange) => Promise<boolean>;
  create: (data: TimetableStructureForm) => Promise<void>;
  update: (data: TimetableStructureForm) => Promise<void>;
  remove: () => Promise<void>;
};

export const TimetableStructureContext = createContext<TimetableStructureContextProps>({
  structure: undefined,
  id: '',
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  canDelete: false,
  confirmChange: async () => true,
  create: async () => {},
  update: async () => {},
  remove: async () => {},
});

export const useTimetableStructure = () => useContext(TimetableStructureContext);

export const WithTimetableStructure: FC<PropsWithChildren> = ({ children }) => {
  const { id: paramId = '' } = useParams<'id'>();
  const { schoolId = '' } = useSchool();
  const { goBack } = useRouter();
  const { showError, showNotification } = useNotifications();
  const { getConfirmation } = useConfirmationDialog();
  const invalidateQueries = useInvalidateListQueriesFor('timetableStructure');
  const { schoolYears, getSchoolYearById, isLoading: isLoadingSchoolYears } = useSchoolYears();

  const createStructure = useCreateTimetableStructureMutation();
  const updateStructure = useUpdateTimetableStructureMutation();
  const deleteStructure = useDeleteTimetableStructureMutation();

  const { data, isFetching } = useGetTimetableStructureQuery(
    { structureId: paramId },
    { enabled: !!paramId, refetchOnMount: 'always', onError: showError },
  );

  const hasTimetablesInUse = useMemo(() => {
    if (!data?.timetable_usage?.length || !schoolYears.length) return false;

    return data.timetable_usage.some((usage) => {
      const year = getSchoolYearById(usage.school_year_id);
      if (!year) return false;

      if (!usage.count) return false;

      return !isDateInPast(year.end);
    });
  }, [data?.timetable_usage, schoolYears, getSchoolYearById]);

  const create = useCallback(
    async (formData: TimetableStructureForm) => {
      if (!schoolId || paramId) return;
      try {
        await createStructure.mutateAsync({
          school_id: schoolId,
          ...formData,
        });

        showNotification({
          textId: 'school-timetableStructures-CreateSuccess',
          values: { structureName: `"${formData.name}"` },
          type: 'success',
        });
        invalidateQueries();
        goBack();
      } catch (e) {
        if (!isErrorByDetails(e as ApiError, [STRUCTURE_NAME_UNIQUENESS_ERROR])) {
          showError(e as ApiError);
        }
        throw e;
      }
    },
    [schoolId, paramId, createStructure, showNotification, invalidateQueries, goBack, showError],
  );

  const update = useCallback(
    async (formData: TimetableStructureForm) => {
      if (!paramId) return;
      try {
        await updateStructure.mutateAsync({
          params: { structureId: paramId },
          data: formData,
        });

        showNotification({
          textId: 'school-timetableStructures-UpdateSuccess',
          values: { structureName: `"${formData.name}"` },
          type: 'success',
        });
        invalidateQueries();
        goBack();
      } catch (e) {
        if (!isErrorByDetails(e as ApiError, [STRUCTURE_NAME_UNIQUENESS_ERROR])) {
          showError(e as ApiError);
          throw e;
        }
      }
    },
    [paramId, updateStructure, showNotification, invalidateQueries, goBack, showError],
  );

  const remove = useCallback(async () => {
    if (!paramId || !data) return;

    const isConfirmed = await getConfirmation({
      textId: 'school-timetableStructures-DeleteConfirmation',
      textValues: { structureName: `"${data.name}"` },
    });

    if (!isConfirmed) return;

    try {
      await deleteStructure.mutateAsync({ structureId: paramId });

      showNotification({
        textId: 'school-timetableStructures-DeleteSuccess',
        values: { structureName: `"${data.name}"` },
        type: 'success',
      });
      invalidateQueries();
      goBack();
    } catch (e) {
      showError(e as ApiError);
    }
  }, [
    paramId,
    data,
    getConfirmation,
    deleteStructure,
    showNotification,
    invalidateQueries,
    goBack,
    showError,
  ]);

  const isSaving = createStructure.isLoading || updateStructure.isLoading;
  const isDeleting = deleteStructure.isLoading;
  const isLoading = !!paramId && (isFetching || isLoadingSchoolYears);

  const { confirmChange } = useTimetableStructureConfirmation();

  const confirmStructureChange = useCallback(
    async (change: StructureChange) => {
      if (!hasTimetablesInUse || !data) return true;

      return confirmChange(data, change);
    },
    [confirmChange, data, hasTimetablesInUse],
  );

  return (
    <TimetableStructureContext.Provider
      value={{
        structure: data,
        id: paramId,
        isLoading,
        isSaving,
        isDeleting,
        canDelete: !hasTimetablesInUse,
        confirmChange: confirmStructureChange,
        create,
        update,
        remove,
      }}
    >
      {children}
    </TimetableStructureContext.Provider>
  );
};
